// @flow
import React, { type Element } from 'react';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, Form, Field } from 'redux-form';
import { InputField, createFieldComponent } from 'angieslist-app-platform/lib/browser/forms';
import { Col, Row, ReduxFormPhoneNumber, ReduxForm } from 'angieslist-react-components';
import StateSelect from 'angieslist-app-platform/lib/browser/stateselect/StateSelect';
import { validation, analytics } from 'angieslist-app-platform';
import { get, replace } from 'lodash';
import { defaultPageProps } from '../../../../app/browser/analyticsDefaultProperties';
import { itemClicked } from '../../../../app/browser/analyticsHelpers';
import { goToError } from './utils/Utils';
import RequiredField from '../../../common/requiredField';
import './EditContactInformation.scss';

type Props = {
  serviceProviderId: string | number,
  handleSubmit: any,
  change: Function,
};

const StateSelectField = createFieldComponent(StateSelect);

const validationRules = {
  firstName: {
    required: true,
    msg: 'Please enter a valid first name',
  },
  lastName: {
    required: true,
    msg: 'Please enter a valid last name',
  },
  primaryPhone: {
    required: true,
    minLength: 10,
  },
  email: {
    required: true,
    pattern: 'email',
    maxLength: 254,
  },
  website: {
    required: false,
    pattern: 'url',
    msg: 'Website format invalid. URL must include http://www or https://www',
    maxLength: 254,
  },
  postalCode: {
    required: true,
    pattern: 'postalCode',
  },
  addressLine1: {
    required: true,
    maxLength: 254,
  },
  addressLine2: {
    required: false,
    maxLength: 254,
  },
  city: {
    required: true,
    maxLength: 254,
  },
  stateId: {
    required: true,
  },
};

export const EditContactInformationComponent =
({ serviceProviderId, handleSubmit, change }: Props): Element<"div"> => (
  <div id="Edit-Contact-Information-Component-Container">
    <div className="page-name" style={{ display: 'flex' }}>
      Contact info
      <RequiredField class="margin-contact-page" />
    </div>
    <Form
      id="contactInformationForm"
      onSubmit={handleSubmit}
      onSubmitFailed={goToError()}
      name="contactInformationForm"
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
    >
      <Row>
        <Col xs={12} className="extra-padding">
          <ReduxForm.Input
            name="businessName"
            label="Business name"
            id="Contact-Info--Business-Name"
            type="text"
            readOnly
          />
        </Col>
      </Row>
      <Row className="extra-padding">
        <Col xs={6}>
          <Field
            name="firstName"
            label="First Name"
            id="Edit-Contact--First-Name"
            component={InputField}
            type="text"
            placeholder="First Name"
          />
        </Col>
        <Col xs={6}>
          <Field
            name="lastName"
            label="Last Name"
            id="Edit-Contact--Last-Name"
            component={InputField}
            type="text"
            placeholder="Last Name"
          />
        </Col>
      </Row>
      <Row className="extra-padding">
        <Col xs={12}>
          <Field
            name="addressLine1"
            label="Address 1"
            id="Edit-Contact--Address-1"
            component={InputField}
            type="text"
            placeholder="Address 1"
          />
        </Col>
      </Row>
      <Row className="extra-padding">
        <Col xs={12}>
          <Field
            name="addressLine2"
            label="Address 2"
            id="Edit-Contact--Address-2"
            component={InputField}
            type="text"
            placeholder="Address 2"
          />
        </Col>
      </Row>
      <Row className="extra-padding">
        <Col xs={8} lg={4}>
          <Field
            name="city"
            label="City"
            id="Edit-Contact--City"
            component={InputField}
            type="text"
            placeholder="City"
          />
        </Col>
        <Col xs={4} lg={4}>
          <Field
            name="stateId"
            label="State"
            id="Edit-Contact--State"
            component={StateSelectField}
            type="select"
            onChange={(event) => {
               // endpoint required the abbreviation and stateId
               // component only allows to get one value at a time
              change('stateAbbreviation', event.currentTarget.options[event.currentTarget.selectedIndex].text);
            }
            }
          />
        </Col>
        <Col xs={8} md={6} lg={4}>
          <Field
            name="postalCode"
            label="ZIP code"
            id="Edit-Contact--Zip-Code"
            component={InputField}
            type="text"
            placeholder="ZIP code"
          />
        </Col>
      </Row>
      <Row className="extra-padding">
        <Col xs={12}>
          <ReduxFormPhoneNumber
            name="primaryPhone"
            id="Edit-Contact--phoneNumber"
            label="Phone"
          />
          <p className="helper-text">This is the phone number where you will receive calls from customers. Your direct phone number will not be displayed and calls will be routed through a virtual phone number. </p>
        </Col>
      </Row>
      <Row className="extra-padding">
        <Col xs={12}>
          <Field
            name="email"
            label="Email"
            id="Edit-Contact--Email"
            component={InputField}
            type="email"
            placeholder="example@example.com"
          />
          <p className="helper-text">This email address will be used to send you notifications about new contacts from customers. Your direct email address will not be displayed to customers.</p>
        </Col>
      </Row>
      <Row className="extra-padding">
        <Col xs={12}>
          <Field
            name="website"
            label="Website or social link (optional)"
            id="Edit-Contact--Website"
            component={InputField}
            type="text"
            placeholder="http://example.com"
          />
        </Col>
      </Row>
    </Form>
  </div>
);

function stripNonDigits(x) {
  return replace(x, /[^\d]/g, '');
}

const stateIds = {
  AL: 1,
  AK: 2,
  AZ: 3,
  AR: 4,
  CA: 5,
  CO: 6,
  CT: 7,
  DE: 8,
  DC: 59,
  FL: 9,
  GA: 10,
  HI: 11,
  ID: 12,
  IL: 13,
  IN: 14,
  IA: 15,
  KS: 16,
  KY: 17,
  LA: 18,
  ME: 19,
  MD: 20,
  MA: 21,
  MI: 22,
  MN: 23,
  MS: 24,
  MO: 25,
  MT: 26,
  NE: 27,
  NV: 28,
  NH: 29,
  NJ: 30,
  NM: 31,
  NY: 32,
  NC: 33,
  ND: 34,
  OH: 35,
  OK: 36,
  OR: 37,
  PA: 38,
  RI: 39,
  SC: 40,
  SD: 41,
  TN: 42,
  TX: 43,
  UT: 44,
  VT: 45,
  VA: 46,
  WA: 47,
  WV: 48,
  WI: 49,
  WY: 50,
};

function extractContactInfo(state) {
  const currentProfileUAPI = get(state.serviceProviderProfile.currentProfileUAPI, 'response');
  const userInfo = get(currentProfileUAPI, 'userInfo');
  const contactInfo = get(currentProfileUAPI, 'contactInfo');
  const address = get(contactInfo, 'address');
  const businessInfo = get(currentProfileUAPI, 'businessInfo');
  const emails = get(contactInfo, 'emails', []);
  const phones = get(contactInfo, 'phoneNumbers', []);
  const primaryEmail = (emails || []).filter(email => email.type === 'PRIMARY');
  const primaryPhone = (phones || []).filter(phone => phone.type === 'PRIMARY');


  return {
    firstName: get(userInfo, 'firstName'),
    lastName: get(userInfo, 'lastName'),
    email: primaryEmail.length > 0 ? primaryEmail[0].email : '',
    website: get(contactInfo, 'website'),
    addressLine1: get(address, 'street1'),
    addressLine2: get(address, 'street2'),
    postalCode: get(address, 'postalCode'),
    city: get(address, 'city'),
    primaryPhone: stripNonDigits(primaryPhone.length > 0 ? primaryPhone[0].phoneNumber : ''),
    stateId: stateIds[get(address, 'state')],
    stateAbbreviation: get(address, 'state'),
    businessName: get(businessInfo, 'businessName'),
  };
}


const editContactInfoAnalytics = {
  page: {
    name: 'SP : My Business : Contact Information',
    properties: {
      ...defaultPageProps,
    },
  },
  elements: {
    'Edit-Contact--*': itemClicked('SP : My Business : Contact Information', 'My Business : Contact'),
  },
};

export default (compose(
  connect(state => ({
    initialValues: extractContactInfo(state),
  })),
  reduxForm({
    form: 'contactInformationForm',
    destroyOnUnmount: false,
    validate: values => validation.validate(values, validationRules).invalidAttrs,
  }),
  analytics({ ruleset: editContactInfoAnalytics }),
  pure,
)(EditContactInformationComponent): any);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/EditContactInformation.js